import Image from 'next/image';
import { Download } from 'lucide-react';
import { getStrapiPdfDownloadUrl } from '@/lib/strapi-media-url';
import { cn } from '@/utils/cn';

export interface PdfDocumentCardItem {
  id: number | string;
  title: string;
  description?: string | null;
  href?: string | null;
}

interface PdfDocumentCardProps {
  item: PdfDocumentCardItem;
  className?: string;
}

export function DocumentGrid({ children }: { children: React.ReactNode }) {
  return (
    <ul role="list" className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {children}
    </ul>
  );
}

export default function PdfDocumentCard({ item, className }: PdfDocumentCardProps) {
  const href = getStrapiPdfDownloadUrl(item.href);
  const isLink = Boolean(href);
  const Wrapper = isLink ? 'a' : 'div';
  const wrapperProps = isLink ? { href: href!, download: '' } : {};

  return (
    <li className={cn('h-full', className)}>
      <Wrapper
        {...wrapperProps}
        className={cn(
          'group relative flex h-full w-full flex-col overflow-hidden rounded-2xl',
          'border border-[#e4e4e4] bg-white shadow-sm',
          'transition-[border-color,box-shadow,transform] duration-300',
          isLink &&
            'cursor-pointer hover:-translate-y-0.5 hover:border-[#ed1c25]/35 hover:shadow-[0_20px_40px_-24px_rgba(237,28,34,0.35)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ed1c25]/50',
        )}
        aria-label={
          isLink ? `${item.title} (download PDF)` : `${item.title} (document coming soon)`
        }
      >
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_top,rgba(237,28,34,0.06)_0%,transparent_55%)] opacity-0 transition-opacity duration-300 group-hover:opacity-100 group-focus-visible:opacity-100"
        />

        <div className="relative z-10 flex flex-1 flex-col p-4 sm:p-5">
          <div className="flex min-h-[104px] flex-1 items-center justify-center rounded-xl bg-[#faf9f7] ring-1 ring-inset ring-[#ebebeb] sm:min-h-[112px]">
            <Image
              src="/images/pdf_icon.svg"
              alt=""
              width={48}
              height={56}
              aria-hidden
              className="h-11 w-auto object-contain transition-transform duration-300 group-hover:scale-105 sm:h-12"
            />
          </div>

          <div className="mt-4 border-t border-[#f0f0f0] pt-4 text-center">
            <p className="line-clamp-3 min-h-[2.75rem] text-sm font-medium leading-snug text-[#1C1917] transition-colors duration-300 group-hover:text-[#0a0a0a]">
              {item.title}
            </p>
            {item.description ? (
              <p className="mt-1 line-clamp-2 text-xs text-[#737373]">{item.description}</p>
            ) : null}
            {isLink ? (
              <span className="mt-3 inline-flex items-center justify-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-[#ed1c25] opacity-80 transition-opacity duration-300 group-hover:opacity-100 sm:text-[11px]">
                Download
                <Download className="h-3 w-3 shrink-0" aria-hidden />
              </span>
            ) : (
              <span className="mt-3 inline-block text-[10px] font-semibold uppercase tracking-[0.14em] text-[#A09A93] sm:text-[11px]">
                Coming soon
              </span>
            )}
          </div>
        </div>
      </Wrapper>
    </li>
  );
}
